import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { shellKeys } from '@app/api/hooks/shell';
import { get, post, put, withPath } from '@app/api/typed';
import { GATEWAY_LIST_TIMEOUT_MS } from '@app/api/client';
import { normalizeInfraConfig } from './InfraModel';
import type {
  GroupTestable,
  InfraApplyResult,
  InfraConfig,
  InfraTestResult,
  ValueField,
} from './InfraModel';

/**
 * A chave do cache CARREGA O SITE (S-e (2)).
 *
 * Sem ele, trocar de cluster no seletor mostraria a configuração do anterior até a
 * releitura chegar — e, pior, um `setQueryData` do Salvar de um site sobrescreveria
 * a leitura do outro. É a mesma razão de a chave de `Identities` carregar o filtro:
 * duas perguntas diferentes, dois cofres.
 *
 * `undefined` é «o site padrão», que é o que toda instalação single-site usa.
 */
export const infraKeys = {
  config: (siteId?: string) => ['infra', 'config', siteId ?? 'default'] as const,
};

/** O `site_id` da query — ausente quando é o padrão. */
const siteParams = (siteId?: string) => (siteId ? { site_id: siteId } : {});

/**
 * A tela inteira de T20 sai daqui (FR-018): grupos, fields, tipos, obrigatórios e
 * origem vêm do que o middleware publica. **Nenhuma lista própria de fields.**
 */
export function useInfraConfig(siteId?: string): UseQueryResult<InfraConfig, ApiError> {
  return useQuery<InfraConfig, ApiError>({
    queryKey: infraKeys.config(siteId),
    // A conformação de forma acontece **aqui**, na entrada do cache, e não em
    // cada componente: o que o middleware publica como texto num campo de lista
    // ou booleano (é o que o real faz) vira lista e booleano antes de a tela
    // existir. Ver `normalizarInfraConfig`.
    queryFn: () =>
      get('/api/v1/infra/config', { params: siteParams(siteId) }).then(normalizeInfraConfig),
  });
}

export interface SaveGroupVars {
  grupo: string;
  fields: Record<string, ValueField>;
  /** O site que recebe a ordem. Ausente = o padrão (S-e (2)). */
  siteId?: string;
}

/**
 * Salvar grupo (FR-021, FR-022, FR-023).
 *
 * A resposta traz a configuração **relida** depois do `PUT /config` — é ela que
 * vai para o cache, para a tela mostrar valor efetivo e origem (ambiente/padrão)
 * como ficaram, não como foram enviados. `nada_a_salvar` vem sem `config`: aí não
 * há o que substituir, e o sucesso é silencioso.
 */
export function useUpdateInfraGroup(): UseMutationResult<
  InfraApplyResult,
  ApiError,
  SaveGroupVars
> {
  const queryClient = useQueryClient();
  return useMutation<InfraApplyResult, ApiError, SaveGroupVars>({
    mutationFn: ({ grupo, fields, siteId }) =>
      // `withPath` monta a URL concreta; o cast devolve o tipo do template para
      // o `put` continuar validando corpo e resposta contra o contrato.
      put(
        withPath('/api/v1/infra/config/{group}', {
          group: grupo,
        }) as '/api/v1/infra/config/{group}',
        {
          params: siteParams(siteId),
          body: { fields },
        },
      ),
    onSuccess: (resultado, { siteId }) => {
      // A releitura que volta do `PUT` entra no mesmo cache que a do `GET`, então
      // passa pela mesma conformação — senão a tela nasce certa e quebra depois
      // de salvar, que é o pior dos dois. E na chave DAQUELE site: escrever na do
      // padrão faria o Salvar de um cluster aparecer na tela do outro.
      if (resultado.config)
        queryClient.setQueryData(infraKeys.config(siteId), normalizeInfraConfig(resultado.config));
      else void queryClient.invalidateQueries({ queryKey: infraKeys.config(siteId) });
      // Prontidão de grupo muda o que a Visão geral e o chip mostram.
      void queryClient.invalidateQueries({ queryKey: shellKeys.shell });
    },
  });
}

/**
 * **Testar conexão** do grupo (FR-019).
 *
 * Dependência fora do ar **não é erro HTTP**: o resultado por verificação
 * (ok · falha · indeterminado) vem em `data.checks`.
 */
export function useTestInfraGroup(
  siteId?: string,
): UseMutationResult<InfraTestResult, ApiError, GroupTestable> {
  return useMutation<InfraTestResult, ApiError, GroupTestable>({
    mutationFn: (grupo) =>
      post(
        withPath('/api/v1/infra/test/{group}', { group: grupo }) as '/api/v1/infra/test/{group}',
        // aim-h4n: the test of the APIM lists the APIs of the gateway in pages, as the import does.
        grupo === 'apim'
          ? { params: siteParams(siteId), timeoutMs: GATEWAY_LIST_TIMEOUT_MS }
          : { params: siteParams(siteId) },
      ),
  });
}
